import React, { useEffect, useState } from "react";
import { FormLabel } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { ActionMeta } from "react-select";
import CreatableSelect from "react-select/creatable";
import { getRecipientDefaultQuestionAndAnswer } from "src/actionCreators/securityQuestionCreators";
import { ISecurityQuestionAnswer } from "src/Core/Domain/Compose/IComposeModel";
import { getUserIdFromEmail } from "src/helper/HelperFunctions";
import { isValidEmailAddress } from "src/helper/Validations";
import { initialRecipientQuestionAnswer, QnAActions } from "src/reducers/recipientQuestionAnswerReducer";
import { RecipientValidationActions } from "src/reducers/recipientValidationReducers";
import { ApplicationState } from "src/store";
import { debounce } from "throttle-debounce";
import { fetchComposeRecipients } from "../../actionCreators/composeActionCreators";
import { ComposeMesssageConstants } from "../../Core/Common/Constants";
import { RecipientType } from "../../Core/Common/Enums";
import { IRecipientModel } from "../../Core/ViewModels/Message/RecipientModel";
import { AppNotifier } from "../Common/Notification/AppNotifier";
import { IMessageSecurityQuestion } from "src/Core/ViewModels/Message/MessageSecurityQuestionModel";
import { ToasterMessages } from "src/helper/ToasterMessages";

interface IRecipientProps {
    toRecipients: IRecipientModel[];
}

const createOption = (value: string, label: string) => ({
    label,
    value,
});

const Recipient: React.FC<IRecipientProps> = props => {
    const dispatch = useDispatch();
    const [recipientOptions, setRecipientOptions] = useState<any[]>([]);
    const [toRecipients, setToRecipients] = useState<any[]>([]);
    const [isLoading, setLoading] = useState<boolean>(false);

    const recipients = useSelector((state: ApplicationState) => state.recipients);
    const recipientValidationState = useSelector((state: ApplicationState) => state.recipientValidationState);
    const securityQuestionState = useSelector((state: ApplicationState) => state.securityQuestionState);

    useEffect(() => {
        getRecipientsFromItem();
    }, []);

    useEffect(() => {
        updateRecipientOptions();
    }, [props.toRecipients]);

    useEffect(() => {
        if (recipients.length > 0) {
            dispatch({
                type: RecipientValidationActions.UPDATE,
                payload: { ...recipientValidationState, isRecipientValid: true },
            });
        } else {
            dispatch({
                type: RecipientValidationActions.UPDATE,
                payload: { isRecipientValid: false, isQnARecipientValid: false },
            });
        }

        Office.context.mailbox.item.to.setAsync(
            recipients.map(x => x.emailAddress),
            asyncResult => {
                if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
                    AppNotifier.Warning(ToasterMessages.WARNING.ADD_RECIPIENT);
                }
            }
        );
    }, [recipients]);

    const getRecipientsFromItem = () => {
        Office.context.mailbox.item.to.getAsync(asyncResult => {
            const _toRecipients = asyncResult.value;
            const _allRecipients: IRecipientModel[] = _toRecipients.map((item, index) => {
                return {
                    emailAddress: item.emailAddress,
                    firstName: item.displayName != item.emailAddress ? item.displayName : "",
                    lastName: "",
                    userId: 0,
                    recipientType: RecipientType.ToRecipient,
                    index: index,
                };
            });
            dispatch({
                type: QnAActions.SET,
                payload: _allRecipients,
            });
        });
    };

    const updateRecipientOptions = () => {
        const toRecipients = props.toRecipients?.map((item: IRecipientModel) => {
            const label =
                item.firstName || item.lastName
                    ? `${item.firstName ?? ""} ${item.lastName ?? ""} <${item.emailAddress}>`
                    : item.emailAddress;
            return createOption(`${item.emailAddress}`, label);
        });
        setToRecipients(toRecipients);
    };

    const handleInputChange = (inputValue: string) => {
        if (inputValue.length < 3) return;
        else {
            setLoading(true);
            dispatch(
                fetchComposeRecipients(inputValue, (data: IRecipientModel[]) => {
                    const options = data.map((item: IRecipientModel) =>
                        createOption(
                            `${item.emailAddress}`,
                            item.firstName || item.lastName
                                ? `${item.firstName ?? ""} ${item.lastName ?? ""} <${item.emailAddress}>`
                                : item.emailAddress
                        )
                    );
                    setRecipientOptions(options);
                    setLoading(false);
                })
            );
        }
    };

    const handleToChange = (newValue: any, actionMeta: ActionMeta<any>) => {
        handleRecipientChange(actionMeta, RecipientType.ToRecipient);
    };

    const getUsersDefaultQuestion = () => {
        return (
            securityQuestionState.securityQuestions.data.find(x => x.isDefault) ||
            securityQuestionState.securityQuestions.data[0]
        );
    };
    const handleRecipientChange = (data: any, type: RecipientType) => {
        const email = data.option?.value?.trim();
        if (recipients.find((x: IRecipientModel) => x.emailAddress === email)) {
            return;
        }
        switch (data.action) {
            case "select-option":
            case "create-option":
                if (isValidEmailAddress(email)) {
                    const label =
                        isValidEmailAddress(data.option.label) && email.includes(data.option.label)
                            ? ""
                            : data.option.label;
                    const defaultQuestion: IMessageSecurityQuestion | undefined = getUsersDefaultQuestion();
                    let toData: IRecipientModel;
                    dispatch(
                        getRecipientDefaultQuestionAndAnswer(
                            email,
                            defaultQuestion?.messageSecurityQuestionId ?? 0,
                            (result: ISecurityQuestionAnswer) => {
                                const answer = !result.answer ? "" : result.answer;
                                const questionData =
                                    securityQuestionState.securityQuestions.data.find(
                                        x => x.messageSecurityQuestionId === result.securityQuestionId
                                    ) ?? getUsersDefaultQuestion();
                                const question: string = questionData?.question;
                                const questionId = questionData?.messageSecurityQuestionId;

                                toData = {
                                    ...initialRecipientQuestionAnswer,
                                    firstName: label.split(" ")[0] ?? "",
                                    lastName: label.split(" ")[1] ?? "",
                                    emailAddress: email,
                                    recipientType: type,
                                    userId: getUserIdFromEmail(recipients, email),
                                    questionId: questionId,
                                    answer: answer,
                                    question: question,
                                    isModified: false,
                                };
                                dispatch({ type: QnAActions.UPDATE, payload: toData });
                            }
                        )
                    );
                    toData = {
                        ...initialRecipientQuestionAnswer,
                        firstName: label.split(" ")[0] ?? "",
                        lastName: label.split(" ")[1] ?? "",
                        emailAddress: email,
                        recipientType: type,
                        userId: getUserIdFromEmail(recipients, email),
                    };
                    dispatch({ type: QnAActions.ADD, payload: toData });

                    if (recipients.length > 0) {
                        dispatch({
                            type: RecipientValidationActions.UPDATE,
                            payload: {
                                ...recipientValidationState,
                                isRecipientValid: true,
                                isQnARecipientValid: false,
                            },
                        });
                    }
                } else {
                    AppNotifier.Warning(ToasterMessages.WARNING.INVALID_EMAIL_FORMAT);
                }
                break;
            case "remove-value":
            case "pop-value": {
                if (data && data.removedValue) {
                    const emailAddress = data.removedValue.value;
                    const index = recipients.findIndex(x => x.emailAddress === emailAddress);
                    dispatch({
                        type: QnAActions.REMOVE,
                        payload: { emailAddress, index },
                    });
                }
                break;
            }
        }
    };

    const handleRecipientQuestionAnswer = (result: ISecurityQuestionAnswer) => {
        const recipient = recipients.find(x => x.emailAddress === result.recipientEmail);
        if (recipient && recipient.index) {
            const data: IRecipientModel = {
                firstName: recipient.firstName,
                lastName: recipient.lastName,
                emailAddress: recipient.emailAddress,
                questionId: result.securityQuestionId,
                question: securityQuestionState.securityQuestions.data.find(
                    x => x.messageSecurityQuestionId === result.securityQuestionId
                )?.question,
                answer: result.answer,
                recipientType: RecipientType.ToRecipient,
            };

            dispatch({
                type: QnAActions.SAVE,
                payload: { data, index: recipient.index },
            });
        }
    };

    return (
        <>
            <div className="to-label-wrapper">
                <FormLabel className="compose-msg-title">To</FormLabel>
                <span className="fas fa-exclamation-circle" title={ComposeMesssageConstants.TO_HELPTEXT}></span>
            </div>

            <div className="row-full-width to-field-compose">
                <CreatableSelect
                    isMulti
                    cacheOptions
                    isClearable={false}
                    placeholder={ComposeMesssageConstants.TO_PLACEHOLDER}
                    blurInputOnSelect={false}
                    hideSelectedOptions={false}
                    allowCreateWhileLoading={true}
                    options={recipientOptions}
                    onChange={handleToChange}
                    onInputChange={debounce(1000, handleInputChange)}
                    formatCreateLabel={(value: string) => value}
                    theme={theme => {
                        return { ...theme, borderRadius: 0 };
                    }}
                    components={{
                        DropdownIndicator: null,
                    }}
                    styles={creatableStyle}
                    value={toRecipients}
                    isLoading={isLoading}
                />
            </div>
        </>
    );
};

export default Recipient;

const creatableStyle = {
    control: (base: any) => ({
        ...base,
        border: "none",
        minHeight: "32px",
        fontSize: "1rem",
        fontWeight: "400",
        lineHeight: "1.5",
        color: "#212529",
    }),
    menu: (base: any) => ({
        ...base,
        fontSize: "13px",
    }),
    placeholder: defaultStyles => {
        return {
            ...defaultStyles,
            color: "#6c757d",
            fontStyle: "italic",
            fontSize: "13px",
        };
    },
};
